import { Platform, Notify } from 'quasar'
import Dialog from 'src/helpers/dialog'
import { makeGuid } from 'src/helpers/globals'
import { Router as router } from 'src/router'
import { store } from 'src/store'
import pkg from '../../package.json'

import { serviceWorker } from 'src/boot/firebase'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { Device } from '@capacitor/device'
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings'

import { put } from 'src/axios/post'

const siteName = process.env.SITE_NAME

export function hasGrantedPermissions () {
  return store.getters['notifications/hasGrantedNotifications']
}

function setGrantedPermission (val = true) {
  store.commit('notifications/setNotificationsGranted', val)
}

export function getDeviceOs () {
  if (Platform.is.android) { return 'Android' }
  else if (Platform.is.ios) { return 'iOS' }
  else if (Platform.is.win) { return 'Windows' }
  else if (Platform.is.mac) { return 'macOS' }
  else if (Platform.is.linux) { return 'Linux' }
  else if (Platform.is.winphone) { return 'Windows Phone' }
  else if (Platform.is.cros) { return 'Chrome OS' }
  else { return 'Unknown Platform' }
}

export function getDeviceBrowser () {
  const agent = window.navigator.userAgent.toLowerCase()
  switch (true) {
    case agent.indexOf('edge') > -1: return 'edge'
    case agent.indexOf('edg') > -1: return 'chromeEdge'
    case agent.indexOf('opr') > -1 && !!window.opr: return 'opera'
    case agent.indexOf('android') > -1:
    case agent.indexOf('chrome') > -1 && !!window.chrome: return 'chrome'
    case agent.indexOf('trident') > -1: return 'ie'
    case agent.indexOf('firefox') > -1: return 'firefox'
    case agent.indexOf('webkit') > -1: // identifier when running through the app
    case agent.indexOf('safari') > -1: return 'safari'
    default: return 'other'
  }
}

/**
 * Get token via Firebase on Capacitor
 * @returns {Promise<unknown>}
 */
const getFirebaseToken = async () => {
  return new Promise((resolve) => {
    const getFirebaseToken = () => {
      return new Promise((resolve) => {
        FirebaseMessaging.getToken({
          serviceWorkerRegistration: serviceWorker
        })
          .then(res => {
            resolve(res.token)
          })
          .catch(() => {
            resolve(null)
          })
      })
    }

    // Called when a new push notification is received.
    FirebaseMessaging.addListener('notificationReceived', (event) => {
      router.push({
        query: {
          notification: event.notification.data.id
        }
      })
    })
    // Called when a new push notification action is performed.
    FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      router.push({
        query: {
          notification: event.notification.data.id
        }
      })
    })
    // Notification received in-app
    FirebaseMessaging.addListener('notificationReceived', (event) => {
      Notify.create({
        color: 'blue-grey-8',
        detail: 'New Notification',
        message: event.notification.title,
        icon: 'error_outline',
        actions: [
          {
            label: 'View Details',
            color: 'blue-grey-1',
            padding: 'xs',
            size: '12px',
            handler () {
              router.push({
                query: {
                  notification: event.notification.data.id
                }
              })
            }
          }
        ]
      })
    })

    FirebaseMessaging.checkPermissions()
      .then(res => {
        if (res.receive === 'granted') {
          resolve(getFirebaseToken())
        }
        else {
          FirebaseMessaging.requestPermissions()
            .then(res => {
              // A token without permission would register the device as if it could be notified
              resolve(res.receive === 'granted' ? getFirebaseToken() : null)
            })
        }
      })
  })
}

/**
 * Current notification permission, normalised to the web Notification.permission values.
 * Native asks the OS (the plugin answers granted / denied / prompt / prompt-with-rationale).
 * A refusal counts as denied even while Android would still allow one more prompt: every
 * caller (layout, settings, notifications popover) goes through here, and re-asking on each
 * of them stacked the same OS prompt several times over.
 * @returns {Promise<'granted'|'denied'|'default'|'blocked'>}
 */
async function permissionState () {
  if (!Platform.is.capacitor) {
    return check()
  }
  const { receive } = await FirebaseMessaging.checkPermissions()
  if (receive === 'granted') {
    return 'granted'
  }
  return receive === 'prompt' ? 'default' : 'denied'
}

/**
 * Make a request to store device details
 * @param withDialog
 * @returns {Promise<void>}
 */
let inFlight = null

export function request (withDialog = false) {
  // Several callers register the device at login (layout, notifications popover, ...).
  // Share one run so the OS permission prompt is shown once, not once per caller.
  if (inFlight && !withDialog) {
    return inFlight
  }
  inFlight = registerDevice(withDialog).finally(() => {
    inFlight = null
  })
  return inFlight
}

async function registerDevice (withDialog) {
  const device = await Device.getInfo()
  const permission = await permissionState()

  if (permission === 'denied' && withDialog) {
    if (Platform.is.capacitor) {
      // There is no "browser site settings" in the app, and the OS will not prompt
      // again once notifications are denied -- the only way back is the app's own
      // settings screen, so take them straight there.
      Dialog({
        title: 'Notifications blocked',
        message: 'You have blocked notifications for ' + siteName + '. Open your device settings to turn them back on.',
        ok: 'Open Settings',
        cancel: true
      })
        .onOk(() => {
          NativeSettings.open({
            optionAndroid: AndroidSettings.AppNotification,
            optionIOS: IOSSettings.App
          })
            .catch(err => console.error('failed to open app settings', err))
        })
    }
    else {
      Dialog({
        title: 'Blocked',
        message: 'You have blocked notifications for ' + siteName + '. You must manually go into your browser site settings to re-enable notifications.'
      })
    }
  }

  if (hasGrantedPermissions() && permission === 'granted') {
    if (withDialog) {
      Dialog({
        title: 'Granted',
        message: 'Notifications have already been granted! You\'re good to go.'
      })
    }
    return
  }

  // In case they spam the "allow" button
  if (hasGrantedPermissions()) {
    return
  }

  const payload = {
    // Once refused there is no token to fetch; asking again would only re-prompt on Android
    device_token: permission === 'denied' ? null : await getFirebaseToken(),
    device_type: !Platform.is.nativeMobile ? 'webapp' : (Platform.is.android ? 'android' : 'ios'),
    browser_name: getDeviceBrowser(),
    browser_version: Platform.is.version,
    guid: makeGuid(),
    device_os: getDeviceOs(),
    app_version: pkg.version,
    device_name: device.name || device.model || device.manufacturer
  }

  const { status } = await put('v1/devices/token', payload)
  if (status === 'success') {
    // Only update the UI for receiving notifications if a device token was sent up
    if (payload.device_token) {
      setGrantedPermission(true)
    }
  }
}

/**
 * Re-register this device after a re-authentication issued a new access token,
 * so the device row follows the new session.
 */
export async function refreshRegistration () {
  setGrantedPermission(false)
  await request()
}

/**
 * Set the ungranted state upon logout
 * (so that device token will be stored for next user
 *  to log in)
 * @type {boolean}
 */
export async function ungrant () {
  setGrantedPermission(false)

  if (Platform.is.capacitor || serviceWorker) {
    await FirebaseMessaging.removeAllListeners()
    await FirebaseMessaging.deleteToken()
  }
}

/**
 * Check that permissions have been set
 * (either denied or granted) - web only; native goes through permissionState()
 * @returns {string}
 */
export function check () {
  const platform = Platform.is

  if (platform.mobile || !serviceWorker) {
    return 'blocked'
  }

  return Notification.permission
}
